import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { Keyword } from '../types';

const PLATFORMS = [
  { id: 'facebook',  label: 'Facebook',  icon: '📘' },
  { id: 'instagram', label: 'Instagram', icon: '📸' },
  { id: 'google',    label: 'Google',    icon: '🔍' },
];

const Keywords: React.FC = () => {
  const [newKeyword, setNewKeyword] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['keywords'],
    queryFn: async () => {
      const res = await api.get('/keywords');
      return res.data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (keyword: string) => {
      await api.post('/keywords', { keyword });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keywords'] });
      setNewKeyword('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (keywordId: string) => {
      await api.delete(`/keywords/${keywordId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['keywords'] }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      await api.put(`/keywords/${id}`, { enabled });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['keywords'] }),
  });

  const platformMutation = useMutation({
    mutationFn: async ({ id, platforms }: { id: string; platforms: string[] }) => {
      await api.put(`/keywords/${id}`, { platforms });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['keywords'] }),
  });

  const keywords: Keyword[] = data?.keywords || [];

  const handleAdd = () => {
    if (newKeyword.trim()) {
      addMutation.mutate(newKeyword.trim());
    }
  };

  const togglePlatform = (kw: Keyword, platformId: string) => {
    const current: string[] = (kw as any).platforms || ['facebook', 'instagram'];
    const updated = current.includes(platformId)
      ? current.filter(p => p !== platformId)
      : [...current, platformId];
    platformMutation.mutate({ id: kw.keyword_id, platforms: updated });
  };

  return (
    <div>
      <h1 className="page-title">キーワード管理</h1>

      <div className="card" style={{ display: 'flex', gap: 12 }}>
        <input
          className="input"
          placeholder="新しいキーワードを入力（例: 副業、投資、転職）"
          value={newKeyword}
          onChange={e => setNewKeyword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          style={{ maxWidth: 400 }}
        />
        <button
          className="btn btn-primary"
          onClick={handleAdd}
          disabled={addMutation.isPending}
        >
          追加
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <div className="loading">読み込み中...</div>
        ) : keywords.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🔍</div>
            <div>キーワードがありません</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>キーワード</th>
                <th>有効</th>
                <th>対象プラットフォーム</th>
                <th>登録日</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {keywords.map(kw => {
                const platforms: string[] = (kw as any).platforms || ['facebook', 'instagram'];
                return (
                  <tr key={kw.keyword_id}>
                    <td style={{ fontWeight: 600, color: '#e2e8f0' }}>{kw.keyword}</td>
                    <td>
                      <span className={`badge ${kw.enabled ? 'badge-green' : 'badge-gray'}`}>
                        {kw.enabled ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {PLATFORMS.map(p => (
                          <button
                            key={p.id}
                            onClick={() => togglePlatform(kw, p.id)}
                            style={{
                              padding: '3px 10px',
                              borderRadius: 12,
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 500,
                              background: platforms.includes(p.id) ? '#1e2d5a' : '#2d3348',
                              color: platforms.includes(p.id) ? '#4f6ef7' : '#64748b',
                              transition: 'all 0.2s',
                            }}
                          >
                            {p.icon} {p.label}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>
                      {kw.created_at?.slice(0, 10)}
                    </td>
                    <td style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-secondary"
                        style={{
                          padding: '4px 12px',
                          fontSize: 12,
                          ...(!kw.enabled && {
                            background: '#064e3b',
                            color: '#34d399',
                            borderColor: '#065f46',
                          }),
                        }}
                        onClick={() => toggleMutation.mutate({ id: kw.keyword_id, enabled: !kw.enabled })}
                      >
                        {kw.enabled ? '無効化' : '有効化'}
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={() => deleteMutation.mutate(kw.keyword_id)}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Keywords;
